if (!customElements.get("td-lp-product-selector")) {
  class TdProductSelector extends HTMLElement {
    constructor() {
      super();     
      // Get all product items
      this.productItems = this.querySelectorAll('.td-lpcp__product-selector--item');
      this.sectionRoot = this.closest('.td-lp-combined-product');
      this.sectionId = this.sectionRoot ? this.sectionRoot.dataset.sectionId : null;
      this.productForm = null;
      this.atcButton = null;
      this.priceElement = null;
      this.comparePriceElement = null;
      this.atcClickHandler = (e) => this.handleATCClick(e);
      this.atcObserver = null;
      // Get toggle buttons
      this.toggleButtons = this.querySelectorAll('.td-lpcp__toggle-input');
      this.hideSubscriptionOption = this.dataset.hideSubscriptionOption === 'true';
      // Store the current active product and purchase type
      this.activeProduct = null;
      this.currentPurchaseType = this.hideSubscriptionOption ? 'onetime' : 'subscription';
    }

    refreshATCElements() {
      let atcScope = this.querySelector('.td-lpcp__atc-wrapper');

      if (!atcScope && this.sectionRoot) {
        atcScope = this.sectionRoot.querySelector(
          `.td-lpcp__atc-wrapper[data-lpcp-section-id="${this.sectionId}"]`
        );
      }

      this.productForm = atcScope ? atcScope.querySelector('.product-form form') : null;
      this.atcButton = atcScope ? atcScope.querySelector('.td-lpcp__atc-button') : null;
      this.priceElement = atcScope ? atcScope.querySelector('[data-atc-price]') : null;
      this.comparePriceElement = atcScope ? atcScope.querySelector('[data-atc-compare-price]') : null;
    }

    connectedCallback() {
      this.refreshATCElements();

      // Add click event listeners to all product items
      this.productItems.forEach(item => {
        item.addEventListener('click', () => this.handleProductClick(item));
      });

      // Add click listener to ATC button
      if (this.atcButton) {
        this.atcButton.addEventListener('click', this.atcClickHandler);
      }

      // Add change listeners to toggle buttons
      this.toggleButtons.forEach(button => {
        button.addEventListener('change', (e) => this.handleToggleChange(e));
      });

      // Set up initial state
      const initialActiveProduct = this.querySelector('.td-lpcp__product-selector--item.td-active');
      if (initialActiveProduct) {
        this.handleProductClick(initialActiveProduct);
      } else {
        // If no active product is set, set the first one as active and handle free gift
        if (this.productItems.length > 0) {
          this.handleProductClick(this.productItems[0]);
        }
      }

      // Wrap bold text in toggle subtext with drawer trigger links (first bold = drawer 1, etc.)
      this.initToggleSubtextDrawerLinks();

      // Handle ATC blocks that may render after this selector in the DOM.
      setTimeout(() => {
        if (this.atcButton) return;
        this.refreshATCElements();
        if (this.atcButton) {
          this.atcButton.addEventListener('click', this.atcClickHandler);
          if (this.activeProduct) {
            this.updatePrices(this.activeProduct);
          }
          return;
        }

        if (this.sectionRoot && !this.atcObserver) {
          this.atcObserver = new MutationObserver(() => {
            this.refreshATCElements();
            if (!this.atcButton) return;

            this.atcButton.addEventListener('click', this.atcClickHandler);
            if (this.activeProduct) {
              this.updatePrices(this.activeProduct);
            }

            this.atcObserver.disconnect();
            this.atcObserver = null;
          });

          this.atcObserver.observe(this.sectionRoot, {
            childList: true,
            subtree: true
          });
        }
      }, 0);
    }

    disconnectedCallback() {
      if (this.atcButton) {
        this.atcButton.removeEventListener('click', this.atcClickHandler);
      }

      if (this.atcObserver) {
        this.atcObserver.disconnect();
        this.atcObserver = null;
      }
    }

    initToggleSubtextDrawerLinks() {
      const wrapper = this.querySelector('.td-lpcp__toggle-subtext--has-drawers');
      if (!wrapper) return;

      const sectionId = wrapper.getAttribute('data-drawer-section-id');
      const instanceId = wrapper.getAttribute('data-drawer-instance');
      const bolds = wrapper.querySelectorAll('strong, b');
      bolds.forEach((el, index) => {
        const drawerIndex = index + 1;
        const link = document.createElement('a');
        link.href = '#';
        link.setAttribute('role', 'button');
        link.className = 'td-lpcp__toggle-subtext-drawer-link';
        link.setAttribute('data-trigger-side-drawer', String(drawerIndex));
        if (sectionId) link.setAttribute('data-drawer-section-id', sectionId);
        if (instanceId) link.setAttribute('data-drawer-instance', instanceId);
        link.innerHTML = el.innerHTML;
        el.innerHTML = '';
        el.appendChild(link);
      });
    }


    handleProductClick(clickedItem) {
      this.refreshATCElements();
      // Remove td-active class from all items
      this.productItems.forEach(item => {
        item.classList.remove('td-active');
      });

      // Add td-active class to clicked item
      clickedItem.classList.add('td-active');

      // Store the active product
      this.activeProduct = clickedItem;

      // Update the form with the selected product's variant ID
      if (this.productForm) {
        const variantId = clickedItem.dataset.variantId;
        const variantInput = this.productForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variantId;
        }
      }

      // Update prices
      this.updatePrices(clickedItem);
      
      // Update all card prices
      this.updateCardPrices();
      
      // Dispatch event for free gift component
      this.dispatchFreeGiftEvent();
    }

    dispatchFreeGiftEvent() {
      // Get the selected product ID and purchase type
      const selectedProductId = this.activeProduct ? this.activeProduct.dataset.productItemId : null;
      
      // Dispatch custom event for free gift component
      const freeGiftEvent = new CustomEvent('free-gift-update', {
        detail: {
          productId: selectedProductId,
          purchaseType: this.currentPurchaseType
        },
        bubbles: true
      });
      
      this.dispatchEvent(freeGiftEvent);
    }

    updatePrices(productItem) {
      this.refreshATCElements();
      const subscriptionPrice = productItem.dataset.subscriptionPrice;
      const regularPrice = productItem.dataset.regularPrice;
      const compareAtPrice = productItem.dataset.compareAtPrice;
      const subscriptionComparePrice = productItem.dataset.subscriptionComparePrice;

      if (this.priceElement) {
        if (this.currentPurchaseType === 'subscription' && subscriptionPrice) {
          this.priceElement.textContent = subscriptionPrice;
        } else if (this.currentPurchaseType === 'onetime' && regularPrice) {
          this.priceElement.textContent = regularPrice;
        }
      }

      if (this.comparePriceElement) {
        if (this.currentPurchaseType === 'subscription' && subscriptionComparePrice && subscriptionComparePrice !== subscriptionPrice) {
          this.comparePriceElement.textContent = subscriptionComparePrice;
          this.comparePriceElement.style.display = '';
        } else if (this.currentPurchaseType === 'onetime' && compareAtPrice && compareAtPrice !== regularPrice) {
          this.comparePriceElement.textContent = compareAtPrice;
          this.comparePriceElement.style.display = '';
        } else {
          this.comparePriceElement.textContent = '';
          this.comparePriceElement.style.display = 'none';
        }
      }
    }

    handleToggleChange(e) {
      this.currentPurchaseType = e.target.value;
      
      // Update prices when toggle changes
      if (this.activeProduct) {
        this.updatePrices(this.activeProduct);
      }
      
      // Update all card prices
      this.updateCardPrices();
      
      // Dispatch event for free gift component
      this.dispatchFreeGiftEvent();
    }

    updateCardPrices() {
      // Update prices in all product cards
      this.productItems.forEach(item => {
        const priceWrapper = item.querySelector('.td-lpcp-price');
        if (!priceWrapper) return;

        const elements = {
          oneTimeSection: priceWrapper.querySelector('.td-lpcp__one-time-price'),
          subscriptionSection: priceWrapper.querySelector('.td-lpcp__subscription-price'),
          regularUnitPrice: priceWrapper.querySelector('[data-lpcp-price-regularunit-price]'),
          subscriptionUnitPrice: priceWrapper.querySelector('[data-lpcp-price-subscription-unit-price]')
        };

        this.toggleElements(elements, this.currentPurchaseType === 'subscription', {
          oneTimeSection: 'flex',
          subscriptionSection: 'flex',
          regularUnitPrice: 'block',
          subscriptionUnitPrice: 'block'
        });
      });
      
      // Update savings badges and descriptions based on purchase type
      this.updateSavingsBadges();
    }

    updateSavingsBadges() {
      const isSubscription = this.currentPurchaseType === 'subscription';

      // Toggle savings badges within each product item, respecting Liquid rendering
      this.productItems.forEach(item => {
        const subBadges = item.querySelectorAll('[data-subscription-savings]');
        const regBadges = item.querySelectorAll('[data-regular-savings]');

        subBadges.forEach(el => {
          el.style.display = isSubscription ? '' : 'none';
        });
        regBadges.forEach(el => {
          el.style.display = isSubscription ? 'none' : '';
        });
      });
      
      // Update descriptions based on purchase type
      this.updateDescriptions();
    }

    updateDescriptions() {
      // Update descriptions in all product cards
      const isSubscription = this.currentPurchaseType === 'subscription';

      this.productItems.forEach(item => {
        const subscriptionEls = item.querySelectorAll('[data-subscription-description]');
        const regularEls = item.querySelectorAll('[data-regular-description]');

        subscriptionEls.forEach(el => {
          el.style.display = isSubscription ? 'block' : 'none';
        });
        regularEls.forEach(el => {
          el.style.display = isSubscription ? 'none' : 'block';
        });
      });

      // Update additional subscription text
      const additionalSubscriptionText = this.querySelector('.td-lpcp__product-selector--additional-subscription-text');
      if (additionalSubscriptionText) {
        additionalSubscriptionText.style.display = this.currentPurchaseType === 'subscription' ? 'block' : 'none';
      }
    }

    // Helper method to toggle elements based on condition
    toggleElements(elements, showSubscription, displayValues) {
      Object.keys(elements).forEach(key => {
        const element = elements[key];
        if (!element) return;

        const isSubscriptionElement = key.includes('subscription') || key.includes('Subscription');
        const shouldShow = showSubscription ? isSubscriptionElement : !isSubscriptionElement;
        const displayValue = displayValues[key] || 'block';
        
        element.style.display = shouldShow ? displayValue : 'none';
      });
    }

    // Helper method to toggle a list of elements
    toggleElementsList(elements, shouldShow, displayValue = 'block') {
      elements.forEach(element => {
        element.style.display = shouldShow ? displayValue : 'none';
      });
    }

    handleATCClick(e) {
      this.refreshATCElements();
      if (!this.activeProduct || !this.productForm) return;

      e.preventDefault();

      const variantId = this.activeProduct.dataset.variantId;

      const variantInput = this.productForm.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = variantId;

      if (this.currentPurchaseType === 'subscription') {
        const existingSellingPlan = this.productForm.querySelector('input[name="selling_plan"]');
        if (existingSellingPlan) existingSellingPlan.remove();

        const sellingPlanId = this.activeProduct.dataset.sellingPlanId;
        if (sellingPlanId) {
          const sellingPlanInput = document.createElement('input');
          sellingPlanInput.type = 'hidden';
          sellingPlanInput.name = 'selling_plan';
          sellingPlanInput.value = sellingPlanId;
          this.productForm.appendChild(sellingPlanInput);
        }

        // TD CHANGE - Route subscription through ProductForm so cart sections refresh in sync
        if (typeof this.productForm.requestSubmit === 'function') {
          this.productForm.requestSubmit();
        } else {
          this.productForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
        // END TD CHANGE
      } else {
        // TD CHANGE - Bypass form submit for one-time to prevent Recharge from re-injecting
        // selling_plan via its submit event listener on the form (PopstarHealth legacy Recharge)
        this.submitOneTimeDirect(variantId);
        // END TD CHANGE
      }
    }

    submitOneTimeDirect(variantId) {
      const sections = [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('sections', sections.map(s => s.section).join(','));
      formData.append('sections_url', window.location.pathname);

      const buttons = document.querySelectorAll('.single-add-to-cart-button');
      buttons.forEach(btn => {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('loading');
      });

      const productFormEl = this.productForm ? this.productForm.closest('product-form') : null;

      fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
        body: formData
      })
        .then(r => r.json())
        .then(response => {
          if (response.status) {
            if (productFormEl && typeof productFormEl.handleErrorMessage === 'function') {
              productFormEl.handleErrorMessage(response.description);
            }
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: variantId,
              errors: response.description,
              message: response.message
            });
            return;
          }

          sections.forEach(({ id, section, selector }) => {
            const container = document.getElementById(id);
            if (!container || !response.sections || !response.sections[section]) return;
            const target = container.querySelector(selector) || container;
            target.innerHTML = new DOMParser()
              .parseFromString(response.sections[section], 'text/html')
              .querySelector(selector).innerHTML;
          });

          if (typeof CartDrawer !== 'undefined') new CartDrawer();
          if (typeof Cart !== 'undefined') new Cart().renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch(console.error)
        .finally(() => {
          buttons.forEach(btn => {
            btn.classList.remove('loading');
            btn.removeAttribute('aria-disabled');
          });
        });
    }

  }

  customElements.define("td-lp-product-selector", TdProductSelector);
}
